import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, Download, Eye, Sparkles, CheckCircle2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { useForm, Controller } from 'react-hook-form';
import { useStore } from '@/store/useStore';
import { generationApi, templatesApi, handleApiError } from '@/services/api';
import { toast } from 'sonner';
import type { Template, GenerateDocumentRequest } from '@/types';

interface FormData {
  [key: string]: any;
}

export default function DocumentGeneration() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { currentProject, llmProvider, setLLMProvider } = useStore();
  
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generatedDoc, setGeneratedDoc] = useState<{ id: number; filepath: string } | null>(null);
  const [formData, setFormData] = useState<FormData>({});

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm();

  useEffect(() => {
    loadTemplates();
    // Load saved form data from localStorage
    const saved = localStorage.getItem(`generation-form-${projectId}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setFormData(parsed);
        Object.entries(parsed).forEach(([key, value]) => {
          setValue(key, value);
        });
      } catch (e) {
        console.error('Failed to load saved form data');
      }
    }
  }, [projectId]);

  const loadTemplates = async () => {
    if (!currentProject) return;
    
    try {
      const data = await templatesApi.list(currentProject.use_case);
      setTemplates(data);
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  const saveFormData = (data: FormData) => {
    setFormData(data);
    localStorage.setItem(`generation-form-${projectId}`, JSON.stringify(data));
  };

  const onSubmitForm = async (data: FormData) => {
    saveFormData(data);
    setStep(3);
  };

  const generateDocument = async () => {
    if (!selectedTemplate || !projectId) return;

    try {
      setGenerating(true);
      setGenerationProgress(0);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setGenerationProgress((prev) => Math.min(prev + 10, 90));
      }, 500);

      const request: GenerateDocumentRequest = {
        template_id: selectedTemplate.id,
        input_data: formData,
        output_format: 'docx',
        language: 'en',
        use_cloud: llmProvider === 'claude',
      };

      const response = await generationApi.generate(Number(projectId), request);

      clearInterval(progressInterval);
      setGenerationProgress(100);
      setGeneratedDoc({ id: response.id, filepath: response.output_file });
      setStep(4);
      toast.success('Document generated successfully!');
      
      // Clear saved form data
      localStorage.removeItem(`generation-form-${projectId}`);
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setGenerating(false);
    }
  };

  const renderVariableInput = (varName: string, varConfig: any) => {
    const { type, description, required, options } = varConfig;

    if (type === 'select' && options) {
      return (
        <div key={varName} className="space-y-2">
          <Label htmlFor={varName}>
            {description || varName} {required && <span className="text-red-500">*</span>}
          </Label>
          <Controller
            name={varName}
            control={control}
            rules={{ required: required ? 'This field is required' : false }}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder={`Select ${description || varName}`} />
                </SelectTrigger>
                <SelectContent>
                  {options.map((option: string) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors[varName] && (
            <p className="text-sm text-destructive">{String(errors[varName]?.message)}</p>
          )}
        </div>
      );
    }

    if (type === 'textarea') {
      return (
        <div key={varName} className="space-y-2">
          <Label htmlFor={varName}>
            {description || varName} {required && <span className="text-red-500">*</span>}
          </Label>
          <Textarea
            id={varName}
            placeholder={description}
            rows={4}
            {...register(varName, { required: required ? 'This field is required' : false })}
          />
          {errors[varName] && (
            <p className="text-sm text-destructive">{String(errors[varName]?.message)}</p>
          )}
        </div>
      );
    }

    if (type === 'boolean') {
      return (
        <div key={varName} className="flex items-center space-x-2">
          <Checkbox id={varName} {...register(varName)} />
          <Label htmlFor={varName} className="cursor-pointer">
            {description || varName}
          </Label>
        </div>
      );
    }

    if (type === 'date') {
      return (
        <div key={varName} className="space-y-2">
          <Label htmlFor={varName}>
            {description || varName} {required && <span className="text-red-500">*</span>}
          </Label>
          <Input
            id={varName}
            type="date"
            {...register(varName, { required: required ? 'This field is required' : false })}
          />
          {errors[varName] && (
            <p className="text-sm text-destructive">{String(errors[varName]?.message)}</p>
          )}
        </div>
      );
    }

    // Default: text input
    return (
      <div key={varName} className="space-y-2">
        <Label htmlFor={varName}>
          {description || varName} {required && <span className="text-red-500">*</span>}
        </Label>
        <Input
          id={varName}
          type={type === 'number' ? 'number' : 'text'}
          placeholder={description}
          {...register(varName, {
            required: required ? 'This field is required' : false,
            valueAsNumber: type === 'number',
          })}
        />
        {errors[varName] && (
          <p className="text-sm text-destructive">{String(errors[varName]?.message)}</p>
        )}
      </div>
    );
  };

  const handleDownload = async () => {
    if (!generatedDoc) return;
    
    try {
      const blob = await generationApi.download(generatedDoc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedTemplate?.name || 'document'}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Document downloaded');
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate(`/projects/${projectId}`)} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Project
      </Button>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {['Select', 'Fill', 'Generate', 'Download'].map((label, index) => (
            <div key={index} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center font-semibold
                    transition-all
                    ${step > index + 1 ? 'bg-primary text-primary-foreground' : ''}
                    ${step === index + 1 ? 'bg-primary text-primary-foreground ring-4 ring-primary/20' : ''}
                    ${step < index + 1 ? 'bg-muted text-muted-foreground' : ''}
                  `}
                >
                  {step > index + 1 ? <CheckCircle2 className="h-5 w-5" /> : index + 1}
                </div>
                <div className="text-xs mt-2 font-medium">{label}</div>
              </div>
              {index < 3 && (
                <div
                  className={`h-1 flex-1 transition-all ${
                    step > index + 1 ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step 1: Template Selection */}
      {step === 1 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">Select Template</h2>
            <p className="text-muted-foreground">
              Choose the type of document you want to generate for {currentProject?.name}
            </p>
          </div>

          {templates.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="h-16 w-16 text-muted-foreground mb-4 opacity-50" />
                <h3 className="text-xl font-semibold mb-2">No templates available</h3>
                <p className="text-muted-foreground text-center max-w-md">
                  No templates found for this project type
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map((template) => (
                <Card
                  key={template.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedTemplate?.id === template.id
                      ? 'border-primary shadow-md ring-2 ring-primary/20'
                      : 'hover:border-primary/50'
                  }`}
                  onClick={() => setSelectedTemplate(template)}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      {template.name}
                    </CardTitle>
                    <CardDescription>{template.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-muted-foreground">
                      {Object.keys(template.variables).length} fields to fill
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={() => setStep(2)} disabled={!selectedTemplate}>
              Next: Fill Information
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Questionnaire */}
      {step === 2 && selectedTemplate && (
        <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">Fill Information</h2>
            <p className="text-muted-foreground">
              Provide details for {selectedTemplate.name}
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Document Details</CardTitle>
              <CardDescription>
                Fields marked with <span className="text-red-500">*</span> are required
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(selectedTemplate.variables).map(([varName, varConfig]) =>
                renderVariableInput(varName, varConfig)
              )}
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button type="button" variant="outline" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button type="submit">Next: Generation Settings</Button>
          </div>
        </form>
      )}

      {/* Step 3: Generation Settings */}
      {step === 3 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">Generation Settings</h2>
            <p className="text-muted-foreground">Choose how to generate your document</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>LLM Provider</CardTitle>
              <CardDescription>Select which AI model to use for generation</CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={llmProvider}
                onValueChange={(value: 'ollama' | 'claude') => setLLMProvider(value)}
              >
                <div className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-accent cursor-pointer">
                  <RadioGroupItem value="ollama" id="ollama" />
                  <Label htmlFor="ollama" className="flex-1 cursor-pointer">
                    <div className="font-medium">Local (Qwen 32B)</div>
                    <div className="text-sm text-muted-foreground">
                      Free • Private • Runs on your computer
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-accent cursor-pointer">
                  <RadioGroupItem value="claude" id="claude" />
                  <Label htmlFor="claude" className="flex-1 cursor-pointer">
                    <div className="font-medium flex items-center gap-2">
                      Cloud (Claude Sonnet 4)
                      <Sparkles className="h-4 w-4 text-yellow-500" />
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Higher quality • ~$0.50 per document
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)}>
              Back
            </Button>
            <Button onClick={generateDocument} disabled={generating}>
              {generating ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Document
                </>
              )}
            </Button>
          </div>

          {generating && (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Generating document with {llmProvider === 'ollama' ? 'Qwen' : 'Claude'}...</span>
                    <span>{generationProgress}%</span>
                  </div>
                  <Progress value={generationProgress} />
                  <p className="text-xs text-muted-foreground">
                    This may take 30-60 seconds depending on the model and document complexity
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Step 4: Download */}
      {step === 4 && generatedDoc && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/20 mb-4">
              <CheckCircle2 className="h-10 w-10 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Document Generated!</h2>
            <p className="text-muted-foreground">
              Your {selectedTemplate?.name} is ready to download
            </p>
          </div>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-center gap-4">
                <Button onClick={handleDownload} size="lg">
                  <Download className="h-4 w-4 mr-2" />
                  Download Document
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => {
                    if (generatedDoc) {
                      window.open(`/api/v1/generate/${generatedDoc.id}/download`, '_blank');
                    }
                  }}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="text-center space-y-3">
            <Button
              variant="outline"
              onClick={() => {
                setStep(1);
                setSelectedTemplate(null);
                setGeneratedDoc(null);
                setGenerationProgress(0);
                reset();
              }}
            >
              Generate Another Document
            </Button>
            <div>
              <Button
                variant="link"
                onClick={() => navigate(`/projects/${projectId}/analyze`)}
              >
                View All Documents →
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
